import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PropTypes from "prop-types";

/* ---------- Custom Tailwind Components ---------- */
const markdownComponents = {
    table: ({ children, ...props }) => (
        <div className="md-table-wrapper w-full overflow-x-auto my-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <table
                className="md-table border-collapse text-sm md:text-base"
                {...props}
            >
                {children}
            </table>
        </div>
    ),

    thead: ({ children, ...props }) => (
        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100" {...props}>
            {children}
        </thead>
    ),

    tbody: ({ children, ...props }) => (
        <tbody className="bg-white dark:bg-gray-900" {...props}>
            {children}
        </tbody>
    ),

    tr: ({ children, ...props }) => (
        <tr
            className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            {...props}
        >
            {children}
        </tr>
    ),

    th: ({ children, ...props }) => (
        <th
            className="px-4 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide border-r border-gray-200 dark:border-gray-700"
            {...props}
        >
            {children}
        </th>
    ),

    td: ({ children, ...props }) => (
        <td
            className="px-4 py-2 text-sm md:text-base text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700"
            {...props}
        >
            {children}
        </td>
    ),

    p: ({ children, ...props }) => (
        <p className="mb-2 last:mb-0 text-gray-900 dark:text-gray-100 leading-relaxed break-words" {...props}>
            {children}
        </p>
    ),

    code: ({ children, ...props }) => (
        <code
            className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200 break-words"
            {...props}
        >
            {children}
        </code>
    ),

    pre: ({ children, ...props }) => (
        <pre
            className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto text-sm md:text-base font-mono text-gray-800 dark:text-gray-100"
            {...props}
        >
            {children}
        </pre>
    ),

    blockquote: ({ children, ...props }) => (
        <blockquote
            className="border-l-4 border-blue-500 pl-4 italic text-gray-700 dark:text-gray-300 my-2"
            {...props}
        >
            {children}
        </blockquote>
    ),

    ul: ({ children, ...props }) => (
        <ul className="list-disc list-inside my-2 space-y-1 text-sm md:text-base" {...props}>
            {children}
        </ul>
    ),

    ol: ({ children, ...props }) => (
        <ol className="list-decimal list-inside my-2 space-y-1 text-sm md:text-base" {...props}>
            {children}
        </ol>
    ),

    li: ({ children, ...props }) => (
        <li className="text-gray-900 dark:text-gray-100 break-words" {...props}>
            {children}
        </li>
    ),

    strong: ({ children, ...props }) => (
        <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props}>
            {children}
        </strong>
    ),

    em: ({ children, ...props }) => (
        <em className="italic text-gray-800 dark:text-gray-200" {...props}>
            {children}
        </em>
    ),

    h1: ({ children, ...props }) => (
        <h1
            className="text-lg sm:text-xl font-bold mb-2 text-gray-900 dark:text-gray-100"
            {...props}
        >
            {children}
        </h1>
    ),

    h2: ({ children, ...props }) => (
        <h2
            className="text-base sm:text-lg font-bold mb-2 text-gray-900 dark:text-gray-100"
            {...props}
        >
            {children}
        </h2>
    ),

    h3: ({ children, ...props }) => (
        <h3
            className="text-sm sm:text-base font-semibold mb-1 text-gray-900 dark:text-gray-100"
            {...props}
        >
            {children}
        </h3>
    ),
};

/* ---------- Main Markdown Renderer ---------- */
export const MarkdownRenderer = ({ content, className = "" }) => (
    <div
        className={`markdown-content text-gray-900 dark:text-gray-100 text-sm sm:text-base md:text-lg leading-relaxed ${className}`}
    >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
        </ReactMarkdown>
    </div>
);

MarkdownRenderer.propTypes = {
    content: PropTypes.string.isRequired,

    className: PropTypes.string,
};

/* ---------- Utility: Strip Markdown to Plain Text ---------- */
export const extractPlainText = (markdownText) => {
    return markdownText
        .replace(/\|/g, " ")
        .replace(/#{1,6}\s+/g, "")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/`(.*?)`/g, "$1")
        .replace(/```[\s\S]*?```/g, "")
        .replace(/^\s*[-*+]\s+/gm, "")
        .replace(/^\s*\d+\.\s+/gm, "")
        .replace(/\n\s*\n/g, "\n")
        .trim();
};

/* ---------- Utility: Detect Quoted Markdown ---------- */
export const containsQuotedMarkdown = (message) => {
    if (typeof message === "object" && message.isQuoted) return true;
    if (typeof message === "string") {
        const hasQuotes = message.includes('"');
        const hasMarkdown =
            message.includes("##") ||
            message.includes("|") ||
            message.includes("**") ||
            message.includes("*") ||
            message.includes("`") ||
            message.includes(">") ||
            message.includes("- ") ||
            message.includes("1. ");
        return hasQuotes && hasMarkdown;
    }
    return false;
};

export default MarkdownRenderer;
